import React, { useRef, useMemo } from 'react';
import { useMouse } from 'react-use';
import styled from '@emotion/styled';

type MouseHandle = (x: number, y: number) => void;

const clap = (min: number, max: number) => (v: number) => {
  if (v < min) {
    return min;
  } else if (v > max) {
    return max;
  }
  return v;
}

export interface PixelBoardProps extends UsePixelBoardProps {
  grid?: boolean;
  children?: React.ReactNode;
  onMouseDown?: MouseHandle;
  onMouseLeave?: MouseHandle;
  onMouseUp?: MouseHandle;
  onMouseMove?: MouseHandle;
  onClick?: MouseHandle;
}

export interface UsePixelBoardProps {
  width: number;
  height: number;
  pixelSize?: number;
}

export function usePixelBoard<T>(props: UsePixelBoardProps & { ref: React.MutableRefObject<Element> }) {
  const {
    ref,
    height,
    width,
  } = props;
  const pixelSize = props.pixelSize ?? 1;
  const { elX, elY } = useMouse(ref);
  const pos: [number, number] = useMemo(() => [
    clap(0, width - 1)(Math.floor(elX / pixelSize)),
    clap(0, height - 1)(Math.floor(elY / pixelSize))
  ], [elX, elY]);
  return pos;
}

export function PixelBoard(props: PixelBoardProps) {
  const {
    height,
    width,
    children,
    onMouseDown,
    onMouseLeave,
    onMouseMove,
    onMouseUp,
    onClick,
  } = props;
  const pixelSize = props.pixelSize ?? 1;
  const grid = props.grid ?? false;
  const ref = useRef<HTMLDivElement>(null);
  const pos = usePixelBoard({ ...props, ref });
  const {
    Container,
    Board,
  } = S;
  const gridStyle = grid ? {
    backgroundImage: 'linear-gradient(#a9abac 1px,transparent 0),linear-gradient(90deg, #a9abac 1px,transparent 0)',
    backgroundSize: `${pixelSize}px ${pixelSize}px`,
    borderRight: '1px solid #a9abac',
    borderBottom: '1px solid #a9abac',
  } : {
      border: '1px solid #a9abac',
    };
  return (
    <Container>
      <Board
        ref={ref}
        style={{
          width: width * pixelSize,
          minWidth: width * pixelSize,
          maxWidth: width * pixelSize,
          height: height * pixelSize,
          minHeight: height * pixelSize,
          maxHeight: height * pixelSize,
          ...gridStyle,
        }}
        onMouseDown={() => onMouseDown ? onMouseDown(...pos) : null}
        onMouseLeave={() => onMouseLeave ? onMouseLeave(...pos) : null}
        onMouseUp={() => onMouseUp ? onMouseUp(...pos) : null}
        onMouseMove={() => onMouseMove ? onMouseMove(...pos) : null}
        onClick={() => onClick ? onClick(...pos) : null}
      />
      {children}
    </Container>

  )
}

const S = {
  Container: styled.div`
    position: relative;
  `,
  Board: styled.div`
    position: absolute;
  `
};
